import { app } from "@/config/env";
import { isLocal } from "@/lib/utils";
import posthog, { type PostHog, type Properties } from "posthog-js";

import type { FeatureFlag } from "./types";

export let posthogInstance: PostHog | undefined;

export const PosthogAnalytics = {
  init: () => {
    if (isLocal) {
      console.log("[PostHog] Development mode – analytics disabled");
      return;
    }

    posthogInstance = posthog.init(app.POSTHOG_API_KEY, {
      autocapture: false,
      disable_session_recording: true, // To start recording, read docs product-analytics.md
    });
  },
  track: (event: string, properties?: Properties) => {
    if (isLocal) return;
    posthogInstance?.capture(event, properties);
  },
  identify: (userId: string, properties?: Properties) => {
    if (isLocal) return;
    posthogInstance?.identify(userId, properties);
    if (userId) {
      posthogInstance?.reloadFeatureFlags();
    }
  },
  reset: () => {
    if (isLocal) return;
    posthogInstance?.reset();
  },
  isFeatureEnabled: (flag: FeatureFlag) => {
    if (isLocal) return false;
    return posthogInstance?.isFeatureEnabled?.(flag) ?? false;
  },
  getFeatureFlag: (flag: FeatureFlag) => {
    if (isLocal) return null;
    return posthogInstance?.getFeatureFlag?.(flag);
  },
  setUserProperties: (id: string, email: string, name: string) => {
    if (isLocal) return;
    posthogInstance?.identify(id, { email, name });
  },
  getIdentifiers: () => {
    if (isLocal) return { distinct_id: undefined, session_id: undefined };
    const session_id = posthogInstance?.get_session_id();
    const distinct_id = posthogInstance?.get_distinct_id();

    return { distinct_id, session_id };
  },
  startSessionRecording: () => {
    if (isLocal) return;
    posthog.startSessionRecording();
  },
  stopSessionRecording: () => {
    if (isLocal) return;
    posthog.stopSessionRecording();
  },
};
